import { modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { ENTITY_NAME_MAX_LENGTH, entityNameZodValidator } from "../commonZodValidators/entityName.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    LoraNetworkProfileFieldsWithDefaults,
    LoraNetworkProfileSchemaTypes,
} from "../types/loraNetworkProfile.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { LoraNetworkProfileSupportedDeviceSelector } from "./subSchemas/loraNetworkProfileSupportedDeviceSelector.js";
import { ResponsivenessSettings } from "./subSchemas/responsivenessSettings.js";
import * as utilities from "./utilities.js";

const SCHEMA_KEY = "LoraNetworkProfile" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: SCHEMA_KEY,
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<LoraNetworkProfile>>(...addSchemaKeyHook(SCHEMA_KEY))
export class LoraNetworkProfile extends SchemaBase<LoraNetworkProfile, LoraNetworkProfileFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({ zodValidator: entityNameZodValidator })
    @prop({ type: () => String, required: true, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name!: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public description?: string;

    @validate({
        childClass: ResponsivenessSettings,
    })
    @prop({ type: () => ResponsivenessSettings, required: true, _id: false })
    public responsivenessSettings!: ResponsivenessSettings;

    @validate({
        childClass: ResponsivenessSettings,
    })
    @prop({ type: () => ResponsivenessSettings, required: true, _id: false })
    public inactiveResponsivenessSettings!: ResponsivenessSettings;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public useConfirmedDownlinks!: boolean;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public queueClassADownlinksInAdvance!: boolean;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public useActiveResponsivenessIfIrrigationInProgress!: boolean;

    @validate({
        childClass: LoraNetworkProfileSupportedDeviceSelector,
    })
    @prop({ type: () => LoraNetworkProfileSupportedDeviceSelector, required: true, _id: false })
    public supportedDeviceSelector!: LoraNetworkProfileSupportedDeviceSelector;

    static getters = {} as const satisfies Record<string, (self: LoraNetworkProfile) => any>;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<LoraNetworkProfileSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<LoraNetworkProfile, LoraNetworkProfileFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

LoraNetworkProfile.dtoValidators = getZodSchemaFromClass(
    LoraNetworkProfile,
) as unknown as DtoValidatorTypes<LoraNetworkProfileSchemaTypes>;
